import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { Map, List, PlusCircle, User, Users, Shield, LogIn, Bookmark, MessageSquare, Bell, UserPlus, Check, X, Heart, Menu, ChevronDown, Settings, FileText, Info, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import HamburgerMenu from './HamburgerMenu';

const FEEDBACK_URL = 'https://forms.gle/xt1xVnc9X8wLWKqt7';

// Profile Picture component for consistent display
function ProfileAvatar({ profile, user, size = 'sm' }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const sizeClasses = {
    sm: 'w-4 h-4 text-[8px]',
    md: 'w-8 h-8 text-sm'
  };

  const initial = (profile?.display_name || user?.email || 'U').charAt(0).toUpperCase();

  if (profile?.avatar_url && !imgFailed) {
    return (
      <img
        src={profile.avatar_url}
        alt={profile?.display_name || 'Profile'}
        className={`${sizeClasses[size]} rounded-full object-cover`}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-dive-neon/20 flex items-center justify-center font-medium text-dive-neon`}>
      {initial}
    </div>
  );
}

// Notification Bell Component
function NotificationBell({ position = 'mobile' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const buttonRef = React.useRef(null);
  const navigate = useNavigate();
  const { 
    notifications, 
    friendRequests, 
    unreadNotificationCount,
    acceptFriendRequest,
    declineFriendRequest,
    markNotificationRead,
    markAllNotificationsRead
  } = useApp();

  const pendingRequestCount = friendRequests?.incoming?.length || 0;
  const totalBadgeCount = pendingRequestCount + (unreadNotificationCount || 0);

  const handleAccept = async (requestId, e) => {
    e.stopPropagation();
    try {
      await acceptFriendRequest(requestId);
    } catch (err) {
      console.error('Error accepting request:', err);
    }
  };

  const handleDecline = async (requestId, e) => {
    e.stopPropagation();
    try {
      await declineFriendRequest(requestId);
    } catch (err) {
      console.error('Error declining request:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read but don't delete
    if (!notification.is_read) {
      await markNotificationRead(notification.id);
    }
    setIsOpen(false);
    
    // Navigate based on notification type
    if (notification.type === 'friend_request' || notification.type === 'follow_request') {
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'friend_accepted' || notification.type === 'follow_accepted' || notification.type === 'new_follower') {
      navigate(`/social/${notification.from_user_id}`);
    } else if (notification.type === 'rating_comment') {
      // Go to My Feed tab in Social page to see the comment on their rating
      navigate('/social?tab=myfeed');
    } else if (notification.type === 'bar_request_approved') {
      // Navigate to the new bar page
      const barId = notification.data?.bar_id || notification.reference_id;
      if (barId) {
        navigate(`/bar/${barId}`);
      } else {
        navigate('/list');
      }
    } else if (notification.type === 'bar_request_rejected') {
      // Just navigate to add bar page so they can try again
      navigate('/add');
    } else if (notification.type === 'badge_earned') {
      // Navigate to profile to see badges
      navigate('/profile');
    }
  };

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
    setIsOpen(!isOpen);
  };

  // Calculate dropdown position based on button location
  const getDropdownStyle = () => {
    if (position === 'desktop' && buttonRect) {
      return {
        position: 'fixed',
        left: '84px', // Sidebar width (80px) + margin
        top: `${Math.max(buttonRect.top - 100, 20)}px`, // Position near the button but clamped
        zIndex: 9999
      };
    }
    return {};
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`relative p-2 rounded-lg transition-all ${
          isOpen ? 'bg-dive-neon/20 text-dive-neon' : 'text-gray-400 hover:text-white'
        }`}
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {totalBadgeCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-dive-amber text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
          </span>
        )}
      </button>

      {isOpen && (position === 'desktop' ? (
        // Use portal for desktop to escape map stacking context
        createPortal(
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-[9998]" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <div 
              className="w-80 bg-dive-dark border border-dive-gray rounded-xl shadow-2xl overflow-hidden"
              style={{
                position: 'fixed',
                left: '84px',
                top: buttonRect ? `${Math.max(buttonRect.top - 100, 20)}px` : '200px',
                zIndex: 9999
              }}
            >
            <div className="p-3 border-b border-dive-gray flex items-center justify-between">
              <h3 className="text-white font-medium">Notifications</h3>
              {unreadNotificationCount > 0 && (
                <button
                  onClick={() => markAllNotificationsRead()}
                  className="text-xs text-dive-neon hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {/* Friend Requests */}
              {friendRequests?.incoming?.length > 0 && (
                <div className="p-2 border-b border-dive-gray">
                  <p className="text-xs text-gray-500 px-2 mb-2">Friend Requests</p>
                  {friendRequests.incoming.map(request => (
                    <div key={request.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-dive-gray/50">
                      <div className="w-8 h-8 rounded-full bg-dive-neon/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-dive-neon text-sm">
                          {(request.from_user?.display_name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">
                          {request.from_user?.display_name || 'User'}
                        </p>
                        <p className="text-gray-500 text-xs">wants to be friends</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => handleDecline(request.id, e)}
                          className="p-1.5 bg-dive-gray text-gray-400 rounded-lg hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleAccept(request.id, e)}
                          className="p-1.5 bg-dive-neon text-white rounded-lg"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Other Notifications */}
              {notifications?.length > 0 ? (
                <div className="divide-y divide-dive-gray/50">
                  {notifications.slice(0, 10).map(notification => {
                    const userName = notification.from_user?.display_name || notification.from_user?.username || 'Someone';
                    
                    return (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full p-3 flex items-start gap-2 text-left hover:bg-dive-gray/30 transition-colors ${
                          !notification.is_read ? 'bg-dive-neon/5' : ''
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notification.type === 'badge_earned' ? 'bg-gradient-to-br from-amber-500/30 to-yellow-500/30' : 'bg-dive-gray'
                        }`}>
                          {notification.type === 'friend_accepted' ? (
                            <UserPlus className="w-4 h-4 text-dive-neon" />
                          ) : notification.type === 'rating_comment' ? (
                            <Heart className="w-4 h-4 text-dive-amber" />
                          ) : notification.type === 'friend_request' ? (
                            <UserPlus className="w-4 h-4 text-blue-400" />
                          ) : notification.type === 'bar_request_approved' ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : notification.type === 'bar_request_rejected' ? (
                            <X className="w-4 h-4 text-red-400" />
                          ) : notification.type === 'badge_earned' ? (
                            <span className="text-lg">🏆</span>
                          ) : (
                            <Bell className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm">
                            {notification.type === 'friend_accepted' && (
                              <><span className="font-medium">{userName}</span> accepted your friend request</>
                            )}
                            {notification.type === 'follow_accepted' && (
                              <><span className="font-medium">{userName}</span> accepted your follow request</>
                            )}
                            {notification.type === 'new_follower' && (
                              <><span className="font-medium">{userName}</span> started following you</>
                            )}
                            {notification.type === 'follow_request' && (
                              <><span className="font-medium">{userName}</span> requested to follow you</>
                            )}
                            {notification.type === 'rating_comment' && (
                              <><span className="font-medium">{userName}</span> commented on your rating</>
                            )}
                            {notification.type === 'friend_request' && (
                              <><span className="font-medium">{userName}</span> sent you a friend request</>
                            )}
                            {notification.type === 'username_changed' && (
                              <>Your username was updated to comply with our policy</>
                            )}
                            {notification.type === 'bar_request_approved' && (
                              <>🎉 Your bar request "<span className="font-medium">{notification.data?.bar_name || 'Unknown'}</span>" was approved! Rate it now →</>
                            )}
                            {notification.type === 'bar_request_rejected' && (
                              <>Your bar request "<span className="font-medium">{notification.data?.bar_name || 'Unknown'}</span>" was not approved</>
                            )}
                            {notification.type === 'badge_earned' && (
                              <>🎉 You earned the "<span className="font-medium">{notification.data?.badge_name || 'New Badge'}</span>" badge!</>
                            )}
                            {!['friend_accepted', 'follow_accepted', 'new_follower', 'follow_request', 'rating_comment', 'friend_request', 'username_changed', 'bar_request_approved', 'bar_request_rejected', 'badge_earned'].includes(notification.type) && (
                              <>You have a new notification</>
                            )}
                          </p>
                          <p className="text-gray-500 text-xs">{timeAgo(notification.created_at)}</p>
                        </div>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-dive-neon rounded-full flex-shrink-0 mt-1" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : pendingRequestCount === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No notifications yet
                </div>
              ) : null}
            </div>

            <Link
              to="/activity"
              onClick={() => setIsOpen(false)}
              className="block p-3 text-center text-sm text-dive-neon hover:bg-dive-gray/30 border-t border-dive-gray"
            >
              View all activity
            </Link>
          </div>
        </>,
        document.body
      )
    ) : (
      // Use portal for mobile too to escape any stacking context issues
      createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown - centered on mobile */}
          <div 
            className="w-[calc(100vw-2rem)] max-w-80 bg-dive-dark border border-dive-gray rounded-xl shadow-2xl overflow-hidden"
            style={{
              position: 'fixed',
              top: '70px', // Below the mobile nav
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999
            }}
          >
          <div className="p-3 border-b border-dive-gray flex items-center justify-between">
            <h3 className="text-white font-medium">Notifications</h3>
            {unreadNotificationCount > 0 && (
              <button
                onClick={() => markAllNotificationsRead()}
                className="text-xs text-dive-neon hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {/* Friend Requests */}
            {friendRequests?.incoming?.length > 0 && (
              <div className="p-2 border-b border-dive-gray">
                <p className="text-xs text-gray-500 px-2 mb-2">Friend Requests</p>
                {friendRequests.incoming.map(request => (
                  <div key={request.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-dive-gray/50">
                    <div className="w-8 h-8 rounded-full bg-dive-neon/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-dive-neon text-sm">
                        {(request.from_user?.display_name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">
                        {request.from_user?.display_name || 'User'}
                      </p>
                      <p className="text-gray-500 text-xs">wants to be friends</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => handleDecline(request.id, e)}
                        className="p-1.5 bg-dive-gray text-gray-400 rounded-lg hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleAccept(request.id, e)}
                        className="p-1.5 bg-dive-neon text-white rounded-lg"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Other Notifications */}
            {notifications?.length > 0 ? (
              <div className="divide-y divide-dive-gray/50">
                {notifications.slice(0, 10).map(notification => {
                  const userName = notification.from_user?.display_name || notification.from_user?.username || 'Someone';
                  
                  return (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full p-3 flex items-start gap-2 text-left hover:bg-dive-gray/30 transition-colors ${
                        !notification.is_read ? 'bg-dive-neon/5' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notification.type === 'badge_earned' ? 'bg-gradient-to-br from-amber-500/30 to-yellow-500/30' : 'bg-dive-gray'
                      }`}>
                        {notification.type === 'friend_accepted' || notification.type === 'follow_accepted' ? (
                          <UserPlus className="w-4 h-4 text-dive-neon" />
                        ) : notification.type === 'new_follower' ? (
                          <UserPlus className="w-4 h-4 text-purple-400" />
                        ) : notification.type === 'follow_request' ? (
                          <UserPlus className="w-4 h-4 text-dive-amber" />
                        ) : notification.type === 'rating_comment' ? (
                          <Heart className="w-4 h-4 text-dive-amber" />
                        ) : notification.type === 'friend_request' ? (
                          <UserPlus className="w-4 h-4 text-blue-400" />
                        ) : notification.type === 'badge_earned' ? (
                          <span className="text-lg">🏆</span>
                        ) : notification.type === 'bar_request_approved' ? (
                          <span className="text-lg">🎉</span>
                        ) : notification.type === 'bar_request_rejected' ? (
                          <span className="text-lg">❌</span>
                        ) : (
                          <Bell className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm">
                          {notification.type === 'friend_accepted' && (
                            <><span className="font-medium">{userName}</span> accepted your friend request</>
                          )}
                          {notification.type === 'follow_accepted' && (
                            <><span className="font-medium">{userName}</span> accepted your follow request</>
                          )}
                          {notification.type === 'new_follower' && (
                            <><span className="font-medium">{userName}</span> started following you</>
                          )}
                          {notification.type === 'follow_request' && (
                            <><span className="font-medium">{userName}</span> requested to follow you</>
                          )}
                          {notification.type === 'rating_comment' && (
                            <><span className="font-medium">{userName}</span> commented on your rating</>
                          )}
                          {notification.type === 'friend_request' && (
                            <><span className="font-medium">{userName}</span> sent you a friend request</>
                          )}
                          {notification.type === 'username_changed' && (
                            <>Your username was updated to comply with our policy</>
                          )}
                          {notification.type === 'badge_earned' && (
                            <>🎉 You earned the "<span className="font-medium">{notification.data?.badge_name || 'New Badge'}</span>" badge!</>
                          )}
                          {notification.type === 'bar_request_approved' && (
                            <>🎉 Your bar request "<span className="font-medium">{notification.data?.bar_name || 'Unknown'}</span>" was approved!</>
                          )}
                          {notification.type === 'bar_request_rejected' && (
                            <>Your bar request "<span className="font-medium">{notification.data?.bar_name || 'Unknown'}</span>" was not approved</>
                          )}
                          {!['friend_accepted', 'follow_accepted', 'new_follower', 'follow_request', 'rating_comment', 'friend_request', 'username_changed', 'badge_earned', 'bar_request_approved', 'bar_request_rejected'].includes(notification.type) && (
                            <>You have a new notification</>
                          )}
                        </p>
                        <p className="text-gray-500 text-xs">{timeAgo(notification.created_at)}</p>
                      </div>
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-dive-neon rounded-full flex-shrink-0 mt-1" />
                      )}
                    </button>
                  );
                })}
              </div>
            ) : pendingRequestCount === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                No notifications yet
              </div>
            ) : null}
          </div>

          <Link
            to="/activity"
            onClick={() => setIsOpen(false)}
            className="block p-3 text-center text-sm text-dive-neon hover:bg-dive-gray/30 border-t border-dive-gray"
          >
            View all activity
          </Link>
        </div>
      </>,
      document.body
    )))}
    </div>
  );
}

function Navigation() {
  const { user, profile, isAdmin, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const desktopDropdownRef = useRef(null);
  const desktopDropdownMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close desktop dropdown on route change
  useEffect(() => {
    setIsDesktopDropdownOpen(false);
  }, [location.pathname]);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideButton = desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target);
      const clickedOutsideMenu = !desktopDropdownMenuRef.current || !desktopDropdownMenuRef.current.contains(event.target);
      if (clickedOutsideButton && clickedOutsideMenu) {
        setIsDesktopDropdownOpen(false);
      }
    };
    if (isDesktopDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDesktopDropdownOpen]);

  const handleDesktopLogout = async () => {
    try {
      await signOut();
      setIsDesktopDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Primary nav items for top bar (reduced set for mobile)
  const navItems = [
    { to: '/', icon: Map, label: 'Map' },
    { to: '/list', icon: List, label: 'Bars' },
    { to: '/social', icon: Users, label: 'Social' },
    { to: '/profile', icon: user ? User : LogIn, label: user ? 'Profile' : 'Login' },
  ];

  // Desktop nav items (includes Lists)
  const desktopNavItems = [
    { to: '/', icon: Map, label: 'Map' },
    { to: '/list', icon: List, label: 'Bars' },
    { to: '/lists', icon: Bookmark, label: 'Lists' },
    { to: '/social', icon: Users, label: 'Social' },
    { to: '/profile', icon: user ? User : LogIn, label: user ? 'Profile' : 'Login' },
  ];

  // Show all nav items - pages will handle auth prompts
  const visibleNavItems = navItems;

  return (
    <>
      {/* Desktop Sidebar */}
      <nav className="hidden md:flex fixed left-0 top-0 h-full w-20 bg-dive-darker border-r border-dive-gray flex-col items-center py-6 z-50" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
        <Link to="/" className="mb-4">
          <img src="/logo-icon.png" alt="Tilted" className="h-10 w-auto bg-transparent" />
        </Link>

        {/* Notification Bell (desktop) - right under logo */}
        {user && (
          <div className="w-14 flex justify-center mb-4">
            <NotificationBell position="desktop" />
          </div>
        )}
        
        <div className="flex flex-col gap-2">
          {desktopNavItems.map(({ to, icon: Icon, label }) => {
            const isProfileLink = to === '/profile';
            const showAvatar = isProfileLink && user && (profile?.avatar_url || profile?.display_name);
            // Redirect to auth if not logged in for profile
            const linkTo = isProfileLink && !user ? '/auth' : to;
            
            return (
              <NavLink
                key={to}
                to={linkTo}
                end={to === '/'}
                className={({ isActive }) =>
                  `w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${
                    isActive
                      ? 'bg-dive-neon text-white shadow-lg shadow-dive-neon/30'
                      : 'text-gray-400 hover:bg-dive-gray hover:text-white'
                  }`
                }
              >
                {showAvatar ? (
                  <ProfileAvatar profile={profile} user={user} size="sm" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
                <span className="text-[10px] font-medium">{label}</span>
              </NavLink>
            );
          })}

          {/* More Dropdown Button - right under Profile */}
          <div ref={desktopDropdownRef}>
          <button
            onClick={() => setIsDesktopDropdownOpen(!isDesktopDropdownOpen)}
            className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${
              isDesktopDropdownOpen
                ? 'bg-dive-gray text-white'
                : 'text-gray-400 hover:bg-dive-gray hover:text-white'
            }`}
          >
            <div className="flex items-center gap-0.5">
              <Menu className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium">More</span>
          </button>
          </div>
        </div>
      </nav>

      {/* Desktop Dropdown Menu - Rendered via Portal */}
      {isDesktopDropdownOpen && createPortal(
        <div 
          ref={desktopDropdownMenuRef}
          className="fixed w-56 bg-dive-darker border border-dive-gray rounded-xl shadow-2xl overflow-hidden z-[9999] max-h-[calc(100vh-100px)] overflow-y-auto"
          style={{
            left: '84px',
            bottom: '24px',
          }}
        >
          <div className="py-2">
            {/* Add a Bar */}
            <Link
              to="/add"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-dive-gray/50 hover:text-white transition-colors"
              onClick={() => setIsDesktopDropdownOpen(false)}
            >
              <PlusCircle className="w-5 h-5" />
              <span>Add a Bar</span>
            </Link>

            {/* Settings */}
            <Link
              to="/settings"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-dive-gray/50 hover:text-white transition-colors"
              onClick={() => setIsDesktopDropdownOpen(false)}
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </Link>

            {/* Privacy Policy */}
            <Link
              to="/privacy"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-dive-gray/50 hover:text-white transition-colors"
              onClick={() => setIsDesktopDropdownOpen(false)}
            >
              <FileText className="w-5 h-5" />
              <span>Privacy Policy</span>
            </Link>

            {/* About Us */}
            <Link
              to="/about"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-dive-gray/50 hover:text-white transition-colors"
              onClick={() => setIsDesktopDropdownOpen(false)}
            >
              <Info className="w-5 h-5" />
              <span>About Us</span>
            </Link>

            {/* Feedback */}
            <Link
              to="/feedback"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-dive-gray/50 hover:text-white transition-colors"
              onClick={() => setIsDesktopDropdownOpen(false)}
            >
              <MessageSquare className="w-5 h-5" />
              <span>Report Bug / Feedback</span>
            </Link>

            {/* Admin - only for admins */}
            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-3 text-dive-amber hover:bg-dive-gray/50 transition-colors"
                onClick={() => setIsDesktopDropdownOpen(false)}
              >
                <Shield className="w-5 h-5" />
                <span>Admin Dashboard</span>
              </Link>
            )}

            {/* Divider */}
            {user && <div className="border-t border-dive-gray my-2" />}

            {/* Log Out - only for logged in users */}
            {user && (
              <button
                onClick={handleDesktopLogout}
                className="flex items-center gap-3 px-4 py-3 w-full text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Log Out</span>
              </button>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Top Navigation */}
      <nav className="md:hidden fixed top-0 left-0 right-0 bg-dive-darker border-b border-dive-gray z-[2000] safe-top">
        <div className="flex items-center h-14">
          {/* Logo - Fixed */}
          <Link to="/" className="flex items-center px-3 flex-shrink-0">
            <img src="/logo-nameplate.png" alt="Tilted" className="h-8 w-auto bg-transparent" />
          </Link>
          
          {/* Nav Items - pinned so tapping Map does not scroll the bar sideways */}
          <div className="flex items-center justify-end gap-0.5 pr-2 flex-1 min-w-0">
            {/* Notification Bell (mobile) */}
            {user && <NotificationBell position="mobile" />}
            
            {visibleNavItems.map(({ to, icon: Icon, label }) => {
              const isProfileLink = to === '/profile';
              const showAvatar = isProfileLink && user && (profile?.avatar_url || profile?.display_name);
              
              return (
                <NavLink
                  key={to}
                  to={isProfileLink && !user ? '/auth' : to}
                  end={to === '/'}
                  style={{ touchAction: 'manipulation' }}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg transition-all flex-shrink-0 ${
                      isActive
                        ? 'text-dive-neon bg-dive-neon/10'
                        : 'text-gray-400'
                    }`
                  }
                >
                  {showAvatar ? (
                    <ProfileAvatar profile={profile} user={user} size="sm" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                  <span className="text-[9px] font-medium">{label}</span>
                </NavLink>
              );
            })}
            
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg transition-all flex-shrink-0 text-gray-400 hover:text-white"
              aria-label="Open menu"
            >
              <Menu className="w-4 h-4" />
              <span className="text-[9px] font-medium">More</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hamburger Menu */}
      <HamburgerMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
}

export default Navigation;
